import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";
import { set } from "lodash";

export interface FluxoCaixaType {
    id: number
    description: string;
    value: string;
    date: string;
    tipoMovimento: number;
    empresaId: number;
    contaFinanceiraId: number;
    contaFinanceiraName: string;
    categoriaFinanceiraId: number;
    categoriaFinanceiraName: string;
    openingBalance?: number;
    finalBalance?: number;
}

export interface CreateFluxoCaixaType {
    description: string;
    value: string;
    date: string;
    tipoMovimento: number;
    empresaId: number;
    contaFinanceiraId: number;
    categoriaFinanceiraId: number;
}

export interface EditFluxoCaixaType {
    id: number;
    description: string;
    value: string;
    date: string;
    tipoMovimento: number;
    empresaId: number;
    contaFinanceiraId: number;
    categoriaFinanceiraId: number;
}

export default function useFluxoCaixa() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listFluxoCaixa, setListFluxoCaixa] = useState<FluxoCaixaType[]>([]);
    const [fluxoCaixaPageCount, setFluxoCaixaPageCount] = useState<number>(0);
    const [initialListFluxoCaixa, setInitialListFluxoCaixa] = useState<FluxoCaixaType[]>([]);
    const [fluxoCaixa, setFluxoCaixa] = useState<FluxoCaixaType>({
        id: 0,
        description: "",
        value: "",
        date: "",
        tipoMovimento: 0,
        empresaId: 0,
        contaFinanceiraId: 0,
        contaFinanceiraName: "",
        categoriaFinanceiraId: 0,
        categoriaFinanceiraName: "",
        openingBalance: 0,
        finalBalance: 0,
    });

    function createFluxoCaixa(createFluxoCaixaType: CreateFluxoCaixaType): Promise<CreateFluxoCaixaType | null> {
        setLoading(true);
        return api
            .post(`/FluxoCaixa/create`, createFluxoCaixaType)
            .then((response) => {
                const createdFluxoCaixa: CreateFluxoCaixaType = response.data;
                showToast("success", "Fluxo de caixa criado com sucesso.");
                setLoading(false);
                return createdFluxoCaixa;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar fluxo de caixa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar fluxo de caixa:", error);
                return null;
            });
    }

    function getAllFluxoCaixaPaged(
        pageNumber?: number,
        pageSize?: number,
        EmpresaId?: number,
        dataInicial?: string,
        dataFinal?: string,
    ): Promise<FluxoCaixaType[] | null> {
        setLoading(true);
        let url = `/FluxoCaixa/getAllFluxoCaixaPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (EmpresaId) {
            url += `&EmpresaId=${EmpresaId}`;
        }

        if (dataInicial) {
            url += `&dataInicial=${dataInicial}`;
        }

        if (dataFinal) {
            url += `&dataFinal=${dataFinal}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListFluxoCaixa(response.data.result);
                setInitialListFluxoCaixa(response.data.result);
                setFluxoCaixaPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar fluxos de caixa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar fluxos de caixa:", error);
                return null;
            });
    }

    function updateFluxoCaixa(updateFluxoCaixaData: EditFluxoCaixaType): Promise<EditFluxoCaixaType | null> {
        setLoading(true);
        return api
            .put(`/FluxoCaixa/${updateFluxoCaixaData.id}`, updateFluxoCaixaData)
            .then((response) => {
                showToast("success", "Fluxo de caixa atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar fluxo de caixa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar fluxo de caixa:", error);
                return null;
            });
    }

    function deleteFluxoCaixa(fluxoCaixaId: number): Promise<FluxoCaixaType | null> {
        setLoading(true);
        return api
            .delete(`/FluxoCaixa/${fluxoCaixaId}`)
            .then(() => {
                setFluxoCaixa({
                    id: 0,
                    description: "",
                    value: "",
                    date: "",
                    tipoMovimento: 0,
                    empresaId: 0,
                    contaFinanceiraId: 0,
                    contaFinanceiraName: "",
                    categoriaFinanceiraId: 0,
                    categoriaFinanceiraName: "",
                    openingBalance: 0,
                    finalBalance: 0,
                });
                showToast("success", "Fluxo de caixa deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar fluxo de caixa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar fluxo de caixa:", error);
                return null;
            });
    }

    return {
        createFluxoCaixa,
        getAllFluxoCaixaPaged,
        updateFluxoCaixa,
        deleteFluxoCaixa,
        setListFluxoCaixa,
        listFluxoCaixa,
        setFluxoCaixaPageCount,
        fluxoCaixaPageCount,
        setInitialListFluxoCaixa,
        initialListFluxoCaixa,
        setFluxoCaixa,
        fluxoCaixa,
    };
}
